/**
 * InstagramFeed Molecule Component
 * Automatic infinite carousel with no controls
 */

'use client'

import { cn } from '@/lib/utils'
import Image from 'next/image'
import { useEffect, useRef } from 'react'

interface InstagramPost {
	id: string
	imageUrl: string
	link: string
	caption: string
	isVideo?: boolean
}

interface InstagramFeedProps {
	posts: InstagramPost[]
	className?: string
	speed?: number // Speed in seconds for one complete scroll
}

export function InstagramFeed({
	posts,
	className,
	speed = 30,
}: InstagramFeedProps) {
	const scrollRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const scrollContainer = scrollRef.current
		if (!scrollContainer) return

		// Auto-scroll animation
		let animationId: number
		let startTime: number | null = null
		const scrollWidth = scrollContainer.scrollWidth / 4 // Width of one set of posts

		const animate = (currentTime: number) => {
			if (!startTime) startTime = currentTime
			const elapsed = currentTime - startTime

			// Calculate scroll position
			const scrollPosition = (elapsed / (speed * 1000)) * scrollWidth
			scrollContainer.scrollLeft = scrollPosition % scrollWidth

			animationId = requestAnimationFrame(animate)
		}

		animationId = requestAnimationFrame(animate)

		// Pause on hover
		const handleMouseEnter = () => {
			cancelAnimationFrame(animationId)
		}

		const handleMouseLeave = () => {
			startTime = null
			animationId = requestAnimationFrame(animate)
		}

		scrollContainer.addEventListener('mouseenter', handleMouseEnter)
		scrollContainer.addEventListener('mouseleave', handleMouseLeave)

		return () => {
			cancelAnimationFrame(animationId)
			scrollContainer.removeEventListener('mouseenter', handleMouseEnter)
			scrollContainer.removeEventListener('mouseleave', handleMouseLeave)
		}
	}, [posts, speed])

	// Duplicate posts 4 times for seamless infinite scroll
	const duplicatedPosts = [...posts, ...posts, ...posts, ...posts]

	return (
		<div className={cn('relative w-full overflow-hidden', className)}>
			{/* Instagram Carousel Container */}
			<div
				ref={scrollRef}
				className='flex overflow-x-hidden'
				style={{ scrollBehavior: 'auto' }}
			>
				{duplicatedPosts.map((post, index) => (
					<div
						key={`${post.id}-${index}`}
						className='relative shrink-0 w-[476px] h-[476px] overflow-hidden group'
					>
						{/* Image */}
						<Image
							src={post.imageUrl}
							alt={post.caption}
							fill
							className='object-contain'
						/>

						{/* Video Icon Overlay */}
						{post.isVideo && (
							<div className='absolute inset-0 flex items-center justify-center z-10 pointer-events-none'>
								<div className='w-[60px] h-[60px] bg-[url("/play-icon.svg")] bg-cover bg-center' />
							</div>
						)}

						{/* Hover Overlay */}
						<a
							href={post.link}
							target='_blank'
							rel='noopener noreferrer'
							className='absolute inset-0 bg-black/0 hover:bg-black/90 transition-all duration-500 z-20 flex items-center justify-center'
							aria-label={post.caption}
						>
							<div className='opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-6 text-white text-center'>
								{/* Instagram icon or engagement stats could go here */}
							</div>
						</a>
					</div>
				))}
			</div>
		</div>
	)
}
